import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import sizeMe from 'react-sizeme';

import { usePrevious } from 'webrix/hooks';

import { SOFTPHONE_PAGE_ROUTES } from '@cloud-wave/neon-common-lib/app';

import { useContactContext } from 'lib/common/contexts/ContactContext';

import { useHistory } from 'lib/common/hooks/useHistory';

import CONTACT_STATES from 'lib/common/constants/contactStates';
import CONTACT_TYPES from 'lib/common/constants/contactTypes';
import { SOFTPHONE_LANDING_ROUTE } from 'lib/common/constants/softphoneDefaultNavOptions';

import SoftphoneNavMenuItem from '../SoftphoneNavMenuItem';
import { SoftPhoneTaskItem } from './components/SoftPhoneTaskItem/SoftPhoneTaskItem';
import TasksPopover from './components/TasksPopover';

import './softphone-task-list.scss';

const TASK_LIST_MINIMUM_HEIGHT = 90;
const TASK_HEIGHT = 40;
const MINIMUM_VISIBLE_TASKS = 1;

const sizeMeHOC = sizeMe({ monitorHeight: true, refreshRate: 16 });

interface ISoftphoneTaskListProps {
  isCreateTaskDisabled: boolean;
  isReversedLayout: boolean;
  size: { height: number };
}

const SoftphoneTaskList = ({ isCreateTaskDisabled, isReversedLayout, size: { height } }: ISoftphoneTaskListProps) => {
  const location = useLocation();
  const { navigate } = useHistory();
  const [visibleTasks, setVisibleTasks] = useState<number>(0);

  const {
    actions: { setSelectedTaskId },
    state: { tasks, selectedTaskId },
  } = useContactContext();

  useEffect(() => {
    const availableTasksSpace = Math.floor((height - TASK_LIST_MINIMUM_HEIGHT) / TASK_HEIGHT);
    const numberOfVisibleTasks = availableTasksSpace + MINIMUM_VISIBLE_TASKS;

    if (!Boolean(tasks.length)) {
      return;
    }

    // avoid showing +1 icon as we show the actual task instead.
    if (tasks?.length - numberOfVisibleTasks <= 1) {
      return setVisibleTasks(tasks.length);
    }

    setVisibleTasks(numberOfVisibleTasks);
  }, [tasks, height]);

  const lastSelectedTaskId = usePrevious(selectedTaskId);
  const lastTaskCount = usePrevious(tasks?.length);

  const pushToTask = () => {
    if (location.pathname === SOFTPHONE_PAGE_ROUTES.TASK) {
      return navigate(SOFTPHONE_PAGE_ROUTES.TASK, { replace: true });
    }

    navigate(SOFTPHONE_PAGE_ROUTES.TASK);
  };

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId);
    pushToTask();
  };

  const selectedTask = tasks.find((task) => task.taskId === selectedTaskId);
  const incomingTask = tasks.find(
    (task) => task.status === CONTACT_STATES.CONNECTING && task.type !== CONTACT_TYPES.CONFERENCE_CALL
  );

  useEffect(() => {
    if (lastSelectedTaskId || !selectedTaskId) {
      return;
    }

    pushToTask();
  }, [selectedTaskId]);

  useEffect(() => {
    const taskCount = tasks?.length;
    const isOnTaskRelatedPage =
      location.pathname === SOFTPHONE_PAGE_ROUTES.TASK || location.pathname === SOFTPHONE_PAGE_ROUTES.LANDING;
    const taskHasBeenRemoved = taskCount < lastTaskCount;

    // We only want to push to task when we have an incoming task or a task is removed when we're on a relevant page (forces a re-render)
    if (!incomingTask && (!taskHasBeenRemoved || !isOnTaskRelatedPage)) {
      return;
    }

    const taskToSelect = incomingTask || tasks[0];

    setSelectedTaskId(taskToSelect?.taskId);

    pushToTask();
  }, [tasks?.length]);

  return (
    <div className="softphone-task-list" data-testid="softphone-task-list">
      {!isCreateTaskDisabled && <hr className="softphone-task-list__separator" />}
      {!Boolean(tasks.length) && (
        <SoftphoneNavMenuItem
          key={SOFTPHONE_LANDING_ROUTE.link}
          link={SOFTPHONE_LANDING_ROUTE.link!}
          name={SOFTPHONE_LANDING_ROUTE.name}
          icon={SOFTPHONE_LANDING_ROUTE.icon}
        />
      )}
      {tasks.slice(0, visibleTasks).map((task) => (
        <SoftPhoneTaskItem
          key={task.taskId}
          isActiveTask={task.taskId === selectedTaskId}
          task={task}
          incomingTask={incomingTask}
          handleTaskClick={handleTaskClick}
          highlightPopover={false}
        />
      ))}
      <TasksPopover
        tasks={tasks.slice(visibleTasks)}
        selectedTask={selectedTask}
        isReversedLayout={isReversedLayout}
        onTaskClick={handleTaskClick}
      />
    </div>
  );
};

export default sizeMeHOC(SoftphoneTaskList);
